import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, PlayCircle } from 'lucide-react';
import dayjs from 'dayjs';
import {
  CreateNooraniTutorialPayload,
  NooraniTutorialRow,
  NooraniTutorialsApi,
  UpdateNooraniTutorialPayload,
} from '@/services/noorani-tutorials.api';
import { NooraniGroupsApi } from '@/services/noorani-groups.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<NooraniTutorialRow>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/**
 * Noorani Tutorials — a video library assigned to a school's own Noorani
 * groups. Distinct from the earlier Fees module's "Noorani Fees"
 * (/fees/noorani) — same word, unrelated concept (fee collection vs a
 * teaching-video library) — kept under its own nav/route/page, no overlap.
 */
export function NooraniTutorialsPage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const qc = useQueryClient();
  const showSchoolPicker = scope !== 'school';

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<NooraniTutorialRow | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; row?: NooraniTutorialRow }>({ open: false });
  const [schoolFilter, setSchoolFilter] = useState('');

  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolFilter]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-noorani-tutorials-filter', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['noorani-tutorials', { ...dt.params, schoolFilter, scope }],
    queryFn: () =>
      NooraniTutorialsApi.list({
        ...dt.params,
        schoolId: showSchoolPicker ? schoolFilter || undefined : undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['noorani-tutorials'] });

  const remove = useMutation({
    mutationFn: (id: string) => NooraniTutorialsApi.remove(id),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'no',
        header: 'No.',
        enableSorting: false,
        cell: (info) => (
          <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
        ),
      }),
      columnHelper.accessor('name', { id: 'name', header: 'Video Name' }),
      columnHelper.display({
        id: 'description',
        header: 'Description',
        cell: (info) => <span className="line-clamp-2 max-w-xs text-xs text-slate-600">{info.row.original.description ?? '—'}</span>,
      }),
      columnHelper.accessor('date', { id: 'date', header: 'Date', cell: (info) => dayjs(info.getValue()).format('DD MMM YYYY') }),
      columnHelper.display({
        id: 'video',
        header: 'Video',
        cell: (info) => (
          <a
            href={info.row.original.youtubeLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
          >
            <PlayCircle className="h-4 w-4" /> Watch
          </a>
        ),
      }),
      ...(showSchoolPicker
        ? [columnHelper.accessor('schoolName', { id: 'schoolName', header: 'School' })]
        : []),
      columnHelper.display({
        id: 'assignedGroups',
        header: 'Assigned Groups',
        cell: (info) => (
          <span className="text-xs text-slate-600">{info.row.original.groupNames.join(', ') || '—'}</span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Action',
        enableSorting: false,
        cell: (info) => (
          <div className="inline-flex items-center gap-1">
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              onClick={() => setEditing(info.row.original)}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => setConfirm({ open: true, row: info.row.original })}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      }),
    ],
    [showSchoolPicker, dt.pagination.pageIndex, dt.pagination.pageSize],
  );

  return (
    <>
      <PageHeader
        title="Noorani Tutorials"
        description="Teaching video library, assigned to Noorani groups."
        actions={
          <button className="btn-primary" onClick={() => setUploadOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> Upload Tutorial Video
          </button>
        }
      />

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No tutorial videos yet."
        toolbar={
          showSchoolPicker && (
            <Select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} className="sm:w-56">
              <option value="">All schools</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )
        }
      />

      <TutorialFormModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSaved={invalidate}
        schools={showSchoolPicker ? schools : undefined}
      />

      {editing && (
        <TutorialFormModal open onClose={() => setEditing(null)} onSaved={invalidate} tutorial={editing} />
      )}

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.row && remove.mutate(confirm.row.id)}
        loading={remove.isPending}
        title="Delete this tutorial?"
        message="This removes the video and its group assignments. This cannot be undone."
        confirmText="Delete"
      />
    </>
  );
}

function TutorialFormModal({
  open,
  onClose,
  onSaved,
  tutorial,
  schools,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  tutorial?: NooraniTutorialRow;
  schools?: { id: string; name: string }[];
}) {
  const isEdit = !!tutorial;
  const [schoolId, setSchoolId] = useState(tutorial?.schoolId ?? '');
  const [name, setName] = useState(tutorial?.name ?? '');
  const [description, setDescription] = useState(tutorial?.description ?? '');
  const [date, setDate] = useState(tutorial?.date ?? new Date().toISOString().slice(0, 10));
  const [youtubeLink, setYoutubeLink] = useState(tutorial?.youtubeLink ?? '');
  const [groupIds, setGroupIds] = useState<Set<string>>(new Set(tutorial?.groupIds ?? []));

  useEffect(() => {
    if (open) {
      setSchoolId(tutorial?.schoolId ?? '');
      setName(tutorial?.name ?? '');
      setDescription(tutorial?.description ?? '');
      setDate(tutorial?.date ?? new Date().toISOString().slice(0, 10));
      setYoutubeLink(tutorial?.youtubeLink ?? '');
      setGroupIds(new Set(tutorial?.groupIds ?? []));
    }
  }, [open, tutorial]);

  const effectiveSchoolId = isEdit ? tutorial!.schoolId : schoolId;
  const { data: groups = [] } = useQuery({
    queryKey: ['noorani-groups-for-tutorial-form', effectiveSchoolId],
    queryFn: () => NooraniGroupsApi.list({ schoolId: effectiveSchoolId || undefined, limit: 100 }).then((r) => r.items),
    enabled: open && !!effectiveSchoolId,
  });

  const toggleGroup = (id: string) => {
    setGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const create = useMutation({
    mutationFn: (payload: CreateNooraniTutorialPayload) => NooraniTutorialsApi.create(payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const update = useMutation({
    mutationFn: (payload: UpdateNooraniTutorialPayload) => NooraniTutorialsApi.update(tutorial!.id, payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const saving = create.isPending || update.isPending;
  const error = errMsg(create.error) ?? errMsg(update.error);

  const valid =
    name.trim().length > 0 &&
    !!date &&
    /^https?:\/\/.+/.test(youtubeLink.trim()) &&
    groupIds.size > 0 &&
    (isEdit || !schools || !!schoolId);

  const submit = () => {
    if (isEdit) {
      update.mutate({
        name: name.trim(),
        description: description.trim() || undefined,
        date,
        youtubeLink: youtubeLink.trim(),
        groupIds: [...groupIds],
      });
    } else {
      create.mutate({
        schoolId: schools ? schoolId : undefined,
        name: name.trim(),
        description: description.trim() || undefined,
        date,
        youtubeLink: youtubeLink.trim(),
        groupIds: [...groupIds],
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Tutorial Video' : 'Upload Tutorial Video'}
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={saving || !valid}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Upload'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {!isEdit && schools && (
          <Field label="School" required>
            <Select value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setGroupIds(new Set()); }}>
              <option value="">— Select school —</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Video Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Qaida Lesson 1" />
        </Field>
        <Field label="Description">
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Date" required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="YouTube Link" required>
          <Input
            value={youtubeLink}
            onChange={(e) => setYoutubeLink(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
          />
        </Field>
        <Field label="Assign to Noorani Groups" required>
          {!effectiveSchoolId ? (
            <div className="text-xs text-slate-400">Select a school first.</div>
          ) : groups.length === 0 ? (
            <div className="text-xs text-slate-400">No Noorani groups for this school yet.</div>
          ) : (
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
              {groups.map((g) => (
                <label key={g.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={groupIds.has(g.id)} onChange={() => toggleGroup(g.id)} />
                  {g.name}
                </label>
              ))}
            </div>
          )}
        </Field>
        {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      </div>
    </Modal>
  );
}
